import React from 'react';
import { Card, ProgressBar, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faCircle,
  faClock,
  faRocket,
  faPhone,
  faCog,
  faGraduationCap,
  faFlagCheckered
} from '@fortawesome/free-solid-svg-icons';

interface OnboardingStatus {
  kickoffCall: boolean;
  systemSetup: boolean;
  agentTraining: boolean;
  goLive: boolean;
}

interface ImplementationStatusCardProps {
  implementationPaid: boolean;
  implementationDate: Date | null;
  onboardingStatus: OnboardingStatus | null;
}

interface Step {
  id: string;
  name: string;
  icon: any;
  description: string;
}

const ONBOARDING_STEPS: Step[] = [
  {
    id: 'payment',
    name: 'Implementation Fee',
    icon: faCheckCircle,
    description: '$2,000 fee paid'
  },
  {
    id: 'kickoffCall',
    name: 'Kickoff Call',
    icon: faPhone,
    description: 'Strategy planning session'
  },
  {
    id: 'systemSetup',
    name: 'System Setup',
    icon: faCog,
    description: 'Platform configuration'
  },
  {
    id: 'agentTraining',
    name: 'Agent Training',
    icon: faGraduationCap,
    description: 'Team onboarding sessions'
  },
  {
    id: 'goLive',
    name: 'Go Live',
    icon: faFlagCheckered,
    description: 'Launch and activation'
  }
];

const ImplementationStatusCard: React.FC<ImplementationStatusCardProps> = ({
  implementationPaid,
  implementationDate,
  onboardingStatus
}) => {
  // Calculate days elapsed since implementation started
  const daysElapsed = implementationDate
    ? Math.floor((new Date().getTime() - new Date(implementationDate).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // Calculate completion percentage
  const getCompletionPercentage = (): number => {
    let completed = implementationPaid ? 1 : 0;

    if (onboardingStatus) {
      if (onboardingStatus.kickoffCall) completed++;
      if (onboardingStatus.systemSetup) completed++;
      if (onboardingStatus.agentTraining) completed++;
      if (onboardingStatus.goLive) completed++;
    }

    return (completed / 5) * 100;
  };

  // Check if a step is completed
  const isStepCompleted = (stepId: string): boolean => {
    if (stepId === 'payment') return implementationPaid;
    if (!onboardingStatus) return false;
    return onboardingStatus[stepId as keyof OnboardingStatus] || false;
  };

  // Get current step (first incomplete step)
  const getCurrentStepIndex = (): number => {
    if (!implementationPaid) return 0;
    if (!onboardingStatus?.kickoffCall) return 1;
    if (!onboardingStatus?.systemSetup) return 2;
    if (!onboardingStatus?.agentTraining) return 3;
    if (!onboardingStatus?.goLive) return 4;
    return 5; // All completed
  };

  const completionPercentage = getCompletionPercentage();
  const currentStepIndex = getCurrentStepIndex();
  const isFullyOnboarded = completionPercentage === 100;

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
        <div>
          <FontAwesomeIcon icon={faRocket} className="me-2" />
          <strong>Implementation Progress</strong>
        </div>
        {daysElapsed !== null && (
          <Badge bg="light" text="dark">
            <FontAwesomeIcon icon={faClock} className="me-1" />
            Day {daysElapsed} of 30
          </Badge>
        )}
      </Card.Header>

      <Card.Body>
        {/* Overall Progress Bar */}
        <div className="mb-4">
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">Overall Progress</span>
            <strong>{Math.round(completionPercentage)}% Complete</strong>
          </div>
          <ProgressBar
            now={completionPercentage}
            variant={isFullyOnboarded ? 'success' : 'primary'}
            style={{ height: '12px' }}
          />
        </div>

        {/* Status Message */}
        {isFullyOnboarded ? (
          <div className="alert alert-success mb-4">
            <FontAwesomeIcon icon={faFlagCheckered} className="me-2" />
            <strong>Congratulations!</strong> Your implementation is complete. You're now live on KairosIQ!
          </div>
        ) : (
          <div className="alert alert-info mb-4">
            <strong>Current Step:</strong> {ONBOARDING_STEPS[currentStepIndex]?.name || 'Getting Started'}
            <br />
            <small className="text-muted">
              {ONBOARDING_STEPS[currentStepIndex]?.description || 'Begin your implementation journey'}
            </small>
          </div>
        )}

        {/* Step-by-Step Progress */}
        <div className="onboarding-steps">
          {ONBOARDING_STEPS.map((step, index) => {
            const isCompleted = isStepCompleted(step.id);
            const isCurrent = index === currentStepIndex && !isFullyOnboarded;

            return (
              <div
                key={step.id}
                className={`d-flex align-items-start mb-3 pb-3 ${
                  index < ONBOARDING_STEPS.length - 1 ? 'border-bottom' : ''
                }`}
              >
                {/* Step Icon */}
                <div className="me-3" style={{ minWidth: '40px', textAlign: 'center' }}>
                  <FontAwesomeIcon
                    icon={isCompleted ? faCheckCircle : step.icon}
                    size="2x"
                    className={
                      isCompleted
                        ? 'text-success'
                        : isCurrent
                        ? 'text-primary'
                        : 'text-muted'
                    }
                  />
                </div>

                {/* Step Details */}
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-1">
                      {step.name}
                      {isCurrent && (
                        <Badge bg="primary" className="ms-2">
                          In Progress
                        </Badge>
                      )}
                      {isCompleted && (
                        <Badge bg="success" className="ms-2">
                          Complete
                        </Badge>
                      )}
                    </h6>
                  </div>
                  <p className="text-muted mb-0 small">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Implementation Timeline */}
        {implementationDate && daysElapsed !== null && (
          <div className="mt-4 pt-3 border-top">
            <div className="row text-center">
              <div className="col-6">
                <div className="text-muted small">Started</div>
                <div className="fw-bold">
                  {new Date(implementationDate).toLocaleDateString()}
                </div>
              </div>
              <div className="col-6">
                <div className="text-muted small">Expected Completion</div>
                <div className="fw-bold">
                  {new Date(
                    new Date(implementationDate).getTime() + 30 * 24 * 60 * 60 * 1000
                  ).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Help Text */}
        {!isFullyOnboarded && (
          <div className="mt-3 p-3 bg-light rounded">
            <small className="text-muted">
              <strong>Need help?</strong> Contact your dedicated onboarding specialist or reach
              out to support@kairosiq.ai for assistance with your implementation.
            </small>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ImplementationStatusCard;
